import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import type { GetPageExperienceQuery } from '@/features/experiences/hooks/useGetPageExperience.generated';
import { LabelType, type ExperienceLabel } from '@/types/gql.generated';
import { createExperienceQueryKey } from '@/utils/queryKeys';

type LabelMetadata = Pick<ExperienceLabel, 'text' | 'sortOrder'>;

const getLabelsKey = (labelType: LabelType): 'labels' | 'whoLabels' =>
  labelType === LabelType.Default ? 'labels' : 'whoLabels';

/**
 * Optimistic cache helpers for an experience's labels/whoLabels, operating on
 * the `getExperience.labels` / `getExperience.whoLabels` slices of the cached
 * page-experience query. Mirrors useCategoryCache.
 */
export const useLabelCache = () => {
  const queryClient = useQueryClient();
  const experience = useEffectiveExperience();
  const queryKey = createExperienceQueryKey(experience.id);

  const setLabels = useCallback(
    (
      labelType: LabelType,
      updater: (labels: ExperienceLabel[]) => ExperienceLabel[]
    ) => {
      queryClient.setQueryData<GetPageExperienceQuery | undefined>(
        queryKey,
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          const key = getLabelsKey(labelType);
          return {
            ...oldData,
            getExperience: {
              ...oldData.getExperience,
              [key]: updater(oldData.getExperience[key]),
            },
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  const getLabel = useCallback(
    (labelType: LabelType, labelId: string) => {
      const data = queryClient.getQueryData<GetPageExperienceQuery>(queryKey);
      const labels = data?.getExperience[getLabelsKey(labelType)] ?? [];
      return labels.find((l) => l.id === labelId);
    },
    [queryClient, queryKey]
  );

  const addLabel = useCallback(
    (labelType: LabelType, labelId: string, metadata: LabelMetadata) => {
      setLabels(labelType, (labels) => [
        { id: labelId, ...metadata },
        ...labels,
      ]);
    },
    [setLabels]
  );

  const removeLabel = useCallback(
    (labelType: LabelType, labelId: string) => {
      setLabels(labelType, (labels) => labels.filter((l) => l.id !== labelId));
    },
    [setLabels]
  );

  const updateLabel = useCallback(
    (
      labelType: LabelType,
      label: Pick<ExperienceLabel, 'id'> & Partial<LabelMetadata>
    ) => {
      setLabels(labelType, (labels) =>
        labels.map((existing) =>
          existing.id === label.id ? { ...existing, ...label } : existing
        )
      );
    },
    [setLabels]
  );

  const setSortOrder = useCallback(
    (labelType: LabelType, ordered: ExperienceLabel[]) => {
      setLabels(labelType, () =>
        ordered.map((label, index) => ({ ...label, sortOrder: index }))
      );
    },
    [setLabels]
  );

  return { getLabel, addLabel, removeLabel, updateLabel, setSortOrder };
};
